import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { deleteContact, updateContact } from "../services/contactService";
import CustomFieldInput from "./customFieldInput";
import {
  contactDefaults,
  fieldRows,
  metricRows,
  normalizeContact,
} from "./contactFormFields";
import { canWriteAnyCid } from "../utils/permissions";

// Standalone contact editor used by the unified Search page. Self-contained:
// no contact-slice coupling, just props in / callbacks out.
const ContactEditDialog = ({ open, contact, onClose, onChanged }) => {
  const contactFields = useSelector(
    (state) => state.settings.value.contactFields
  );
  const auth = useSelector((state) => state.auth.value);
  const contactsWritable = canWriteAnyCid(auth);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({ defaultValues: contactDefaults });

  useEffect(() => {
    if (open && contact) {
      reset(normalizeContact(contact));
    }
  }, [open, contact, reset]);

  const onSave = async (data) => {
    setSaving(true);
    try {
      await updateContact({ ...data, id: contact.id });
      if (onChanged) {
        await onChanged();
      }
      onClose(false);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await deleteContact(contact.id);
      if (onChanged) {
        await onChanged();
      }
      onClose(false);
    } finally {
      setDeleting(false);
    }
  };

  if (!contact) {
    return null;
  }

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={() => onClose(false)}>
      <DialogTitle>
        {contact.name || "Contact"}
        <Typography variant="body2" color="text.secondary">
          ID #{contact.id}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {fieldRows.map((row) => (
            <Grid item xs={12} md={6} key={row.name}>
              <Controller
                name={row.name}
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label={row.label} />
                )}
              />
            </Grid>
          ))}
          {contactFields
            .filter((f) => !f.archived)
            .map((def) => (
              <Grid item xs={12} md={6} key={`cf-${def.id}`}>
                <Controller
                  name={`custom_fields.${def.internal_name}`}
                  control={control}
                  render={({ field }) => (
                    <CustomFieldInput
                      def={def}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
            ))}
          <Grid item xs={12}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth multiline minRows={4} label="Notes" />
              )}
            />
          </Grid>
          {metricRows.map((row) => (
            <Grid item xs={6} md={3} key={row.name}>
              <Controller
                name={row.name}
                control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" label={row.label} fullWidth />
                )}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          color="error"
          variant="outlined"
          onClick={onDelete}
          disabled={deleting || saving || !contactsWritable}
        >
          {deleting ? "Deleting..." : "Delete"}
        </Button>
        <Button color="inherit" onClick={() => onClose(false)} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSave)}
          disabled={!isDirty || saving || !contactsWritable}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactEditDialog;
